/**
 * mapper for option values from IOptionMapped
 */

import {
	Option,
	OptionItem,
	OptionValue,
	TrafficType,
	ProductSegment,
	UnitType,
	UserRole
} from 'api';
import { generateUniqueKey } from '../Functions';

export interface IOptionItem extends OptionItem {
	[key: string]: any;
}
export interface IOptionMapped {
	optionId?: number;
	key?: string;
	optionTypeId?: number;
	trafficType?: TrafficType;
	name?: string;
	discount?: number;
	amount?: number;
	active?: boolean;
	value?: string | null;
	price?: number;
	discountedPrice?: number;
	cost?: number;
	maxDiscount?: number;
	units?: number;
	unitType?: UnitType;
	costPerUnit?: number;
	userRole?: UserRole[] | null;
	productSegment?: ProductSegment[] | null;
}

export interface IOption extends Option {
	key?: string;
}

export const mapOptionsStoreToOffer = (options: IOptionMapped[]): OptionItem[] =>
	options.map((o) => {
		return {
			optionId: Number(o.optionId),
			value: String(o.value),
			optionTypeId: o.optionTypeId,
			discount: o.discount,
			amount: Number(o.amount),
			price: Number(o.price)
		};
	});

export const mapOptionsApiToStore = (options?: Option[]): IOptionMapped[] => {
	let mappedOptions: IOptionMapped[] = [];

	if (options && options.length) {
		options.forEach((o: Option) => {
			o.optionValues?.forEach((oVal: OptionValue) => {
				mappedOptions.push({
					optionId: o.id,
					key: generateUniqueKey(),
					optionTypeId: o.optionTypeId,
					trafficType: o.trafficType,
					name: o.name ?? '',
					active: o.active,
					value: String(oVal.value),
					price: oVal.price,
					maxDiscount: Number(oVal.maxDiscount || 0),
					cost: Number(oVal.cost || 0),
					units: oVal.units || 0,
					unitType: oVal.unitType,
					costPerUnit: oVal.costPerUnit || 0,
					userRole: oVal.userRole,
					productSegment: oVal.productSegment
				});
			});
		});
	}
	return mappedOptions;
};

export const mapOptionApiToStore = (option?: Option): IOptionMapped[] => {
	let mappedOptions: IOptionMapped[] = [];

	if (option && Object.keys(option).length > 0) {
		option.optionValues?.forEach((oVal: OptionValue) => {
			mappedOptions.push({
				optionId: option.id,
				key: generateUniqueKey(),
				optionTypeId: option.optionTypeId,
				name: option.name ?? '',
				active: option.active,
				value: String(oVal.value),
				price: oVal.price,
				discount: oVal.maxDiscount || 0,
				cost: oVal.cost || 0,
				units: oVal.units || 0,
				unitType: oVal.unitType,
				costPerUnit: oVal.costPerUnit || 0,
				userRole: oVal.userRole,
				productSegment: oVal.productSegment
			});
		});
	}
	return mappedOptions;
};

export const mapActiveOptionsToApi = (
	activeOptions: IOptionMapped[],
	activeOptionTypeId?: number
): Option => {
	return {
		id: activeOptions[0].optionId,
		name: activeOptions[0].name,
		optionTypeId: activeOptionTypeId,
		active: activeOptions[0].active,
		trafficType: activeOptions[0].trafficType,
		optionValues: activeOptions.map((opt) => ({
			value: opt.value,
			price: Number(opt.price),
			maxDiscount: opt.maxDiscount || 0,
			cost: opt.cost || 0,
			units: Number(opt.units || 0),
			unitType: opt.unitType,
			costPerUnit: Number(opt.costPerUnit || 0),
			userRole: opt.userRole,
			productSegment: opt.productSegment
		}))
	};
};

export const mapOption = (optionFrom: IOptionMapped, optionTo?: IOptionMapped) => {
	let optionProps: IOptionMapped = {
		optionId: Number(optionFrom?.optionId),
		key: optionFrom?.key,
		optionTypeId: optionFrom?.optionTypeId,
		name: optionFrom?.name,
		value: String(optionFrom?.value || ''),
		price: optionFrom?.price,
		maxDiscount: optionFrom?.maxDiscount,
		trafficType: optionFrom?.trafficType
	};
	if (optionTo) {
		optionProps.discount = optionTo.discount;
		optionProps.amount = optionTo.amount;
	}
	return optionProps;
};
